import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AUTH_STORAGE_KEY, SERVER_URL } from "../socket";

function PublicProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const email = useMemo(() => searchParams.get("email")?.trim() || "", [searchParams]);

  useEffect(() => {
    if (!email) {
      setError("No email provided in the profile link.");
      setLoading(false);
      return;
    }

    const loadProfile = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${SERVER_URL}/api/public/profile?email=${encodeURIComponent(email)}`);
        const text = await res.text();
        let data = null;

        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          throw new Error("Profile service is currently unavailable.");
        }

        if (!res.ok) {
          throw new Error(data?.error || "Unable to load profile.");
        }
        setProfile(data);
      } catch (err) {
        setError(err.message || "Unable to load profile.");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [email]);

  const handleAddContact = async () => {
    if (!profile?.userId) return;

    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const token = parsed?.token;

    if (!token) {
      navigate(`/login?next=${encodeURIComponent(`/profile?email=${encodeURIComponent(email)}`)}`);
      return;
    }

    const defaultName = profile.fullName || profile.userId?.split("@")[0] || "";
    const alias = window.prompt("Save contact as:", defaultName);
    if (alias === null) return;

    setAdding(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/contacts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          contactId: profile.userId,
          alias: alias.trim(),
        }),
      });
      const text = await res.text();
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        throw new Error("Contact service is currently unavailable.");
      }

      if (!res.ok) {
        throw new Error(data?.error || "Failed to add contact.");
      }
      setAdded(true);
      window.alert(`✅ ${alias.trim() || profile.userId} added to contacts`);
    } catch (err) {
      window.alert(err.message || "Failed to add contact.");
    } finally {
      setAdding(false);
    }
  };

  const initials = (profile?.fullName || profile?.userId || "U")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #020617, #111827)", color: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 520, background: "rgba(15, 23, 42, 0.96)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 24, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading profile…</div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: "center", color: "#fda4af" }}>{error}</div>
        ) : !profile ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>No profile found.</div>
        ) : (
          <>
            <div style={{ height: 140, background: profile.coverImageUrl ? `url(${profile.coverImageUrl}) center/cover` : "linear-gradient(135deg, #1d4ed8, #7c3aed)", position: "relative" }}>
              <div style={{ position: "absolute", inset: 0, background: "rgba(2,6,23,0.35)" }} />
            </div>

            <div style={{ padding: "0 24px 24px", marginTop: -40 }}>
              <div style={{ width: 88, height: 88,marginTop: 40, borderRadius: "50%", border: "4px solid #0f172a", overflow: "hidden", background: "linear-gradient(135deg, #f57011, #ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: "#fff", marginBottom: 14 }}>
                {profile.profileImageUrl ? <img src={profile.profileImageUrl} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
              </div>

              <h3 style={{ margin: "0 0 6px", fontSize: 24 ,color: "#e2e8f0"}}>{profile.fullName || profile.userId || "User"}</h3>
              <p style={{ margin: "0 0 8px", color: "#cbd5e1", fontSize: 14 }}>{profile.userId || profile.email || "No email"}</p>
              {profile.profileNumberDisplay ? <p style={{ margin: "0 0 12px", color: "#f59e0b", fontSize: 13 }}>Kriva ID: {profile.profileNumberDisplay}</p> : null}

              {profile.aboutUs ? <p style={{ margin: "0 0 14px", color: "#e2e8f0", lineHeight: 1.6 }}>{profile.aboutUs}</p> : null}

              {profile.website ? (
                <a href={profile.website} target="_blank" rel="noreferrer" style={{ display: "inline-block", marginBottom: 16, color: "#60a5fa", textDecoration: "none" }}>
                  {profile.website}
                </a>
              ) : null}

              <button
                type="button"
                onClick={handleAddContact}
                disabled={adding}
                style={{ width: "100%", padding: "12px 16px", background: added ? "#16a34a" : "linear-gradient(135deg, #2563eb, #3b82f6)", color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: adding ? "wait" : "pointer" }}
              >
                {adding ? "Adding…" : added ? "Added to Contacts" : "Add to Contact"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PublicProfilePage;
